import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Card,
  CardHeader,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import AdminService from "../../../service/AdminService";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import Loader from "../../loader/Loader";

const today = new Date();
today.setHours(0, 0, 0, 0);

const schema = yup.object().shape({
  sessionCategory: yup
    .number()
    .transform((value, originalValue) => (originalValue === "" ? undefined : value))
    .required("Session category is required"),
  
  visibilityRule: yup
    .number()
    .transform((value, originalValue) => (originalValue === "" ? undefined : value))
    .required("Visibility rule is required"),

  startWeekNumber: yup.number()
    .nullable()
    .transform((curr, orig) => orig === '' ? null : curr)
    .when(["visibilityRule", "sessionCategory"], {
    is: (visRule, category) => parseInt(visRule) === 2 || parseInt(category) === 2,
    then: (schema) => schema.typeError("Start week is required").required("Start week is required").min(0, "Week must be at least 0"),
    otherwise: (schema) => schema.notRequired(),
  }),

  endWeekNumber: yup.number()
    .nullable()
    .transform((curr, orig) => orig === '' ? null : curr)
    .when(["visibilityRule", "sessionCategory"], {
    is: (visRule, category) => parseInt(visRule) === 2 || parseInt(category) === 2,
    then: (schema) => schema.typeError("End week is required").required("End week is required").min(1, "Week must be at least 1"),
    otherwise: (schema) => schema.notRequired(),
  }),

  // Program fields
  title: yup
    .string()
    .required("Title is required")
    .min(3, "Title must be at least 3 characters")
    .max(100, "Title must be at most 100 characters"),

  brief: yup
    .string()
    .required("Brief description is required")
    .min(10, "Brief must be at least 10 characters")
    .max(255, "Brief must be at most 255 characters"),

  imageUrl: yup
    .string()
    .required("Image URL is required")
    .url("Must be a valid URL"),

  price: yup.number()
    .nullable()
    .transform((curr, orig) => orig === '' ? null : curr)
    .when("sessionTypeId", {
    is: (val) => parseInt(val) === 2 || parseInt(val) === 3,
    then: (schema) =>
      schema
        .required("Price is required")
        .min(1, "Price must be greater than 0"),
    otherwise: (schema) => schema.notRequired(),
  }),

  sellingPrice: yup.number()
    .nullable()
    .transform((curr, orig) => orig === '' ? null : curr)
    .when("sessionTypeId", {
    is: (val) => parseInt(val) === 2 || parseInt(val) === 3,
    then: (schema) =>
      schema
        .required("Selling price is required")
        .test(
          "sellingPrice",
          "Selling price must be less than or equal to price",
          function (value) {
            return value <= this.parent.price;
          }
        ),
    otherwise: (schema) => schema.notRequired(),
  }),

  trimesterNo: yup.number().when(["sessionCategory", "visibilityRule"], {
    is: (category, visRule) => parseInt(category) === 1 && parseInt(visRule) === 1,
    then: (schema) =>
      schema
        .typeError("Trimester number is required")
        .required("Trimester number is required")
        .min(1, "Trimester must be between 1 and 3")
        .max(3, "Trimester must be between 1 and 3"),
    otherwise: (schema) => schema.notRequired(),
  }),

  // Session fields
  sessionDate: yup
    .date()
    .required("Session date is required")
    .min(today, "Session date must be today or in the future"),

  sessionStartTime: yup.string().required("Session start time is required"),

  sessionDuration: yup.number().required("Session duration is required"),

  trainerId: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value
    )
    .required("Trainer is required"),

  active: yup.boolean(),

  sessionTypeId: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value
    )
    .required("Session type is required"),
    
  masterSessionId: yup.number().nullable().transform((value, originalValue) =>
      originalValue === "" ? undefined : value
  ).notRequired(),
  
  lastEnrollmentDate: yup.date()
    .nullable()
    .transform((curr, orig) => orig === '' ? null : curr)
    .when("sessionTypeId", {
    is: (val) => parseInt(val) === 3,
    then: (schema) => schema.required("Last enrollment date is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const AddSession = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [trainers, setTrainers] = useState([]);
  const [groupedSessions, setGroupedSessions] = useState([]);
  const user = useSelector((state) => state.user.user);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onBlur",
    defaultValues: {
      sessionCategory: 1,
      visibilityRule: 1,
      startWeekNumber: "",
      endWeekNumber: "",
      // Program defaults
      title: "",
      brief: "",
      imageUrl: "",
      price: 0,
      sellingPrice: 0,
      trimesterNo: 1,
      // Session defaults
      sessionDate: new Date().toISOString().split("T")[0],
      sessionStartTime: "",
      sessionDuration: 60,
      trainerId: "",
      sessionTypeId: "",
      masterSessionId: "",
      lastEnrollmentDate: "",
      active: true,
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const trainersResponse = await AdminService.getTrainers(
          user?.access_token,
          1
        );
        setTrainers(trainersResponse.data);
        
        const groupedSessionsResponse = await AdminService.getGroupedSessions(user?.access_token);
        setGroupedSessions(groupedSessionsResponse.data);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Error fetching data", { duration: 4000 });
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [user?.access_token]);

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);

      // Then, create the session
      const selectedDate = new Date(data.sessionDate);
      selectedDate.setHours(12, 0, 0, 0);

      const sessionData = {
        sessionCategory: parseInt(data.sessionCategory),
        visibilityRule: parseInt(data.sessionCategory) === 2 ? 2 : parseInt(data.visibilityRule),
        startWeekNumber: (parseInt(data.visibilityRule) === 2 || parseInt(data.sessionCategory) === 2) ? parseInt(data.startWeekNumber) : null,
        endWeekNumber: (parseInt(data.visibilityRule) === 2 || parseInt(data.sessionCategory) === 2) ? parseInt(data.endWeekNumber) : null,
        title: data.title,
        brief: data.brief,
        imageUrl: data.imageUrl,
        price: parseInt(data.sessionTypeId) === 1 ? 0 : data.price,
        sellingPrice: parseInt(data.sessionTypeId) === 1 ? 0 : data.sellingPrice,
        trimesterNo: (parseInt(data.sessionCategory) === 1 && parseInt(data.visibilityRule) === 1) ? data.trimesterNo : 1,
        sessionDate: selectedDate.toISOString().split("T")[0],
        sessionStartTime: data.sessionStartTime,
        sessionDuration: data.sessionDuration,
        masterSessionId: parseInt(data.sessionTypeId) === 2 && data.masterSessionId ? parseInt(data.masterSessionId) : null,
        lastEnrollmentDate: parseInt(data.sessionTypeId) === 3 ? new Date(data.lastEnrollmentDate).toISOString().split("T")[0] : null,
        trainer: {
          trainerId: parseInt(data.trainerId),
        },
        sessionType: {
          sessionTypeId: parseInt(data.sessionTypeId),
        },
        active: data.active,
        sessionStatus: { sessionStatusId: 1 },
      };

      await AdminService.addSession(user?.access_token, sessionData);
      toast.success("Session added successfully", {
        duration: 4000,
      });
      navigate(`/admin/sessions`);
    } catch (error) {
      console.error("Error adding session:", error);
      toast.error("Error adding session", { duration: 4000 });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-bold text-gray-900">Add Session</h1>
      </div>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Card className="shadow-sm mb-6">
          <CardHeader className="bg-white border-b py-3">
            <h3 className="text-xs font-medium text-gray-900">
              Session Information
            </h3>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <FormGroup>
                <Label className="text-xs font-medium text-gray-700">
                  Session Category <span className="text-red-500">*</span>
                </Label>
                <Controller
                  name="sessionCategory"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="select"
                      className={`mt-1 ${errors.sessionCategory ? "border-red-500" : ""}`}
                    >
                      <option value="1">Prenatal</option>
                      <option value="2">Postnatal</option>
                    </Input>
                  )}
                />
                {errors.sessionCategory && (
                  <span className="text-xs text-red-500">{errors.sessionCategory.message}</span>
                )}
              </FormGroup>

              <FormGroup>
                <Label className="text-xs font-medium text-gray-700">
                  Visibility Rule <span className="text-red-500">*</span>
                </Label>
                <Controller
                  name="visibilityRule"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="select"
                      className={`mt-1 ${errors.visibilityRule ? "border-red-500" : ""}`}
                    >
                      {parseInt(watch("sessionCategory")) === 1 && <option value="1">Trimester Based</option>}
                      <option value="2">Week Based</option>
                    </Input>
                  )}
                />
                {errors.visibilityRule && (
                  <span className="text-xs text-red-500">{errors.visibilityRule.message}</span>
                )}
              </FormGroup>

              {parseInt(watch("sessionCategory")) === 1 && parseInt(watch("visibilityRule")) === 1 && (
                <FormGroup>
                  <Label className="text-xs font-medium text-gray-700">
                    Trimester Number <span className="text-red-500">*</span>
                  </Label>
                  <Controller
                    name="trimesterNo"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="select"
                        className={`mt-1 ${errors.trimesterNo ? "border-red-500" : ""}`}
                      >
                        <option value="1">First Trimester</option>
                        <option value="2">Second Trimester</option>
                        <option value="3">Third Trimester</option>
                      </Input>
                    )}
                  />
                  {errors.trimesterNo && (
                    <span className="text-xs text-red-500">{errors.trimesterNo.message}</span>
                  )}
                </FormGroup>
              )}

              {(parseInt(watch("visibilityRule")) === 2 || parseInt(watch("sessionCategory")) === 2) && (
                <>
                  <FormGroup>
                    <Label className="text-xs font-medium text-gray-700">
                      Start Week <span className="text-red-500">*</span>
                    </Label>
                    <Controller
                      name="startWeekNumber"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          className={`mt-1 ${errors.startWeekNumber ? "border-red-500" : ""}`}
                        />
                      )}
                    />
                    {errors.startWeekNumber && (
                      <span className="text-xs text-red-500">{errors.startWeekNumber.message}</span>
                    )}
                  </FormGroup>
                  
                  <FormGroup>
                    <Label className="text-xs font-medium text-gray-700">
                      End Week <span className="text-red-500">*</span>
                    </Label>
                    <Controller
                      name="endWeekNumber"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          className={`mt-1 ${errors.endWeekNumber ? "border-red-500" : ""}`}
                        />
                      )}
                    />
                    {errors.endWeekNumber && (
                      <span className="text-xs text-red-500">{errors.endWeekNumber.message}</span>
                    )}
                  </FormGroup>
                </>
              )}
              <FormGroup>
                <Label className="text-xs font-medium text-gray-700">
                  Title <span className="text-red-500">*</span>
                </Label>
                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      className={`mt-1 ${errors.title ? "border-red-500" : ""}`}
                    />
                  )}
                />
                {errors.title && (
                  <span className="text-xs text-red-500">
                    {errors.title.message}
                  </span>
                )}
              </FormGroup>

              <FormGroup>
                <Label className="text-xs font-medium text-gray-700">
                  Brief Description <span className="text-red-500">*</span>
                </Label>
                <Controller
                  name="brief"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="textarea"
                      className={`mt-1 ${errors.brief ? "border-red-500" : ""}`}
                    />
                  )}
                />
                {errors.brief && (
                  <span className="text-xs text-red-500">
                    {errors.brief.message}
                  </span>
                )}
              </FormGroup>

              <FormGroup>
                <Label className="text-xs font-medium text-gray-700">
                  Image URL <span className="text-red-500">*</span>
                </Label>
                <Controller
                  name="imageUrl"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      className={`mt-1 ${
                        errors.imageUrl ? "border-red-500" : ""
                      }`}
                    />
                  )}
                />
                {errors.imageUrl && (
                  <span className="text-xs text-red-500">
                    {errors.imageUrl.message}
                  </span>
                )}
              </FormGroup>

              <FormGroup>
                <Label className="text-xs font-medium text-gray-700">
                  Trainer <span className="text-red-500">*</span>
                </Label>
                <Controller
                  name="trainerId"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="select"
                      className={`mt-1 ${
                        errors.trainerId ? "border-red-500" : ""
                      }`}
                    >
                      <option value="">Select Trainer</option>
                      {trainers.map((trainer) => (
                        <option
                          key={trainer.trainerId}
                          value={trainer.trainerId}
                        >
                          {trainer.trainerName}
                        </option>
                      ))}
                    </Input>
                  )}
                />
                {errors.trainerId && (
                  <span className="text-xs text-red-500">
                    {errors.trainerId.message}
                  </span>
                )}
              </FormGroup>

              <FormGroup>
                <Label className="text-xs font-medium text-gray-700">
                  Session Date <span className="text-red-500">*</span>
                </Label>
                <Controller
                  name="sessionDate"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="date"
                      min={new Date().toISOString().split("T")[0]}
                      className={`mt-1 ${
                        errors.sessionDate ? "border-red-500" : ""
                      }`}
                    />
                  )}
                />
                {errors.sessionDate && (
                  <span className="text-xs text-red-500">
                    {errors.sessionDate.message}
                  </span>
                )}
              </FormGroup>

              <FormGroup>
                <Label className="text-xs font-medium text-gray-700">
                  Start Time <span className="text-red-500">*</span>
                </Label>
                <Controller
                  name="sessionStartTime"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="time"
                      step={60}
                      className={`mt-1 ${
                        errors.sessionStartTime ? "border-red-500" : ""
                      }`}
                    />
                  )}
                />
                {errors.sessionStartTime && (
                  <span className="text-xs text-red-500">
                    {errors.sessionStartTime.message}
                  </span>
                )}
              </FormGroup>

              <FormGroup>
                <Label className="text-xs font-medium text-gray-700">
                  Duration (mins) <span className="text-red-500">*</span>
                </Label>
                <Controller
                  name="sessionDuration"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="select"
                      className={`mt-1 ${
                        errors.sessionDuration ? "border-red-500" : ""
                      }`}
                    >
                      <option value="30">30</option>
                      <option value="45">45</option>
                      <option value="60">60</option>
                      <option value="90">90</option>
                      <option value="120">120</option>
                    </Input>
                  )}
                />
                {errors.sessionDuration && (
                  <span className="text-xs text-red-500">
                    {errors.sessionDuration.message}
                  </span>
                )}
              </FormGroup>

              <FormGroup>
                <Label className="text-xs font-medium text-gray-700">
                  Session Type <span className="text-red-500">*</span>
                </Label>
                <Controller
                  name="sessionTypeId"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="select"
                      className={`mt-1 ${
                        errors.sessionTypeId ? "border-red-500" : ""
                      }`}
                    >
                      <option value="">Select Session Type</option>
                      <option value="1">Monthly</option>
                      <option value="2">Normal</option>
                      <option value="3">Grouped</option>
                    </Input>
                  )}
                />
                {errors.sessionTypeId && (
                  <span className="text-xs text-red-500">
                    {errors.sessionTypeId.message}
                  </span>
                )}
              </FormGroup>

              {watch("sessionTypeId") === "3" && (
                <FormGroup>
                  <Label className="text-xs font-medium text-gray-700">
                    Last Enrollment Date <span className="text-red-500">*</span>
                  </Label>
                  <Controller
                    name="lastEnrollmentDate"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="date"
                        min={new Date().toISOString().split("T")[0]}
                        className={`mt-1 ${errors.lastEnrollmentDate ? "border-red-500" : ""}`}
                      />
                    )}
                  />
                  {errors.lastEnrollmentDate && (
                    <span className="text-xs text-red-500">{errors.lastEnrollmentDate.message}</span>
                  )}
                </FormGroup>
              )}

              {watch("sessionTypeId") === "2" && (
                <FormGroup>
                  <Label className="text-xs font-medium text-gray-700">
                    Master Session
                  </Label>
                  <Controller
                    name="masterSessionId"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="select"
                        className={`mt-1 ${errors.masterSessionId ? "border-red-500" : ""}`}
                      >
                        <option value="">None (Independent)</option>
                        {groupedSessions.map(s => (
                          <option key={s.sessionId} value={s.sessionId}>{s.title}</option>
                        ))}
                      </Input>
                    )}
                  />
                  {errors.masterSessionId && (
                    <span className="text-xs text-red-500">{errors.masterSessionId.message}</span>
                  )}
                </FormGroup>
              )}

              {(watch("sessionTypeId") === "2" || watch("sessionTypeId") === "3") && (
                <>
                  <FormGroup>
                    <Label className="text-xs font-medium text-gray-700">
                      Price (₹) <span className="text-red-500">*</span>
                    </Label>
                    <Controller
                      name="price"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          className={`mt-1 ${
                            errors.price ? "border-red-500" : ""
                          }`}
                        />
                      )}
                    />
                    {errors.price && (
                      <span className="text-xs text-red-500">
                        {errors.price.message}
                      </span>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <Label className="text-xs font-medium text-gray-700">
                      Selling Price (₹) <span className="text-red-500">*</span>
                    </Label>
                    <Controller
                      name="sellingPrice"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          className={`mt-1 ${
                            errors.sellingPrice ? "border-red-500" : ""
                          }`}
                        />
                      )}
                    />
                    {errors.sellingPrice && (
                      <span className="text-xs text-red-500">
                        {errors.sellingPrice.message}
                      </span>
                    )}
                  </FormGroup>
                </>
              )}

              <FormGroup className="flex items-center mt-4">
                <Controller
                  name="active"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <Input
                      {...field}
                      type="checkbox"
                      checked={value}
                      onChange={(e) => onChange(e.target.checked)}
                      className="mt-1"
                    />
                  )}
                />
                <Label className="ml-2 text-xs font-medium text-gray-700">
                  Session Active
                </Label>
                {errors.active && (
                  <span className="text-xs text-red-500">
                    {errors.active.message}
                  </span>
                )}
              </FormGroup>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <Button
                type="button"
                color="danger"
                onClick={() => navigate(`/admin/sessions`)}
              >
                Cancel
              </Button>
              <Button type="submit" color="primary">
                Create Session
              </Button>
            </div>
          </CardBody>
        </Card>
      </Form>
      {isLoading && <Loader />}
    </div>
  );
};

export default AddSession;
